import { useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { createSignup } from "@/lib/api";
import { getRef, buildWhatsAppShareLink } from "@/lib/referral";
import { Loader2, Upload, Camera, CheckCircle2, ShieldCheck, MapPin, Share2, UserPlus } from "lucide-react";

import { Utensils, Palmtree } from "lucide-react";

// Event formats. Each has a recurring weekday + time.
const FORMATS = {
  dinner: { value: "dinner", label: "Dinner", weekday: 3, time: "8:00 PM", icon: Utensils },
  beachclub: { value: "beachclub", label: "Beach club", weekday: 6, time: "3:00 PM", icon: Palmtree },
};

const GENDERS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

// Show the next 2 occurrences of each format (i.e. the succeeding 2 weeks).
const OCCURRENCES_PER_FORMAT = 2;

function nextOccurrences(weekday, count) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  let diff = (weekday - d.getDay() + 7) % 7;
  if (diff === 0) diff = 7; // always the next one, never today
  d.setDate(d.getDate() + diff);
  const out = [];
  for (let i = 0; i < count; i += 1) {
    out.push(new Date(d));
    d.setDate(d.getDate() + 7);
  }
  return out;
}

function startOfWeek(date) {
  // Monday as start of week
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const back = (day + 6) % 7; // days since Monday
  d.setDate(d.getDate() - back);
  return d;
}

function relDateLabel(date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((startOfDay(date) - today) / (24 * 60 * 60 * 1000));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  return date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function weekLabel(date) {
  const thisMonday = startOfWeek(new Date());
  const slotMonday = startOfWeek(date);
  const weeks = Math.round((slotMonday - thisMonday) / (7 * 24 * 60 * 60 * 1000));
  if (weeks <= 0) return "This week";
  if (weeks === 1) return "Next week";
  if (weeks === 2) return "In 2 weeks";
  return `In ${weeks} weeks`;
}

function generateSlots() {
  const slots = [];
  Object.values(FORMATS).forEach((f) => {
    nextOccurrences(f.weekday, OCCURRENCES_PER_FORMAT).forEach((dt) => {
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, "0");
      const d = String(dt.getDate()).padStart(2, "0");
      slots.push({
        id: `${f.value}_${y}-${m}-${d}`,
        format: f.value,
        formatLabel: f.label,
        time: f.time,
        icon: f.icon,
        iso: `${y}-${m}-${d}`,
        dateObj: dt,
        dateLabel: relDateLabel(dt),
        group: weekLabel(dt),
      });
    });
  });
  slots.sort((a, b) => a.dateObj - b.dateObj);
  // Group into ordered sections
  const sections = [];
  slots.forEach((s) => {
    let sec = sections.find((x) => x.group === s.group);
    if (!sec) {
      sec = { group: s.group, items: [] };
      sections.push(sec);
    }
    sec.items.push(s);
  });
  return sections;
}

const initial = {
  name: "",
  age: "",
  gender: "",
  country: "United States",
  country_code: "+1",
  phone: "",
  event: "",
  event_date: "",
  hostel: "",
  photo_data_url: "",
  women_only: false,
  plus_one: false,
  plus_one_gender: "",
};

export default function SignupDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const set = (k, v) =>
    setForm((f) => {
      const next = { ...f, [k]: v };
      // If gender is no longer female, ensure women-only is reset
      if (k === "gender" && v !== "female") next.women_only = false;
      // If plus_one is turned off, clear the gender
      if (k === "plus_one" && !v) next.plus_one_gender = "";
      // If event changes, clear the previously selected date
      if (k === "event") next.event_date = "";
      return next;
    });

  const EVENTS = Object.values(FORMATS).map((f) => ({
    value: f.value,
    label: f.label,
    blurb: f.label === "Dinner" ? "Dinners on Wednesday, 8:00 PM." : "Beach club on Saturday, 3:00 PM.",
    emoji: f.label === "Dinner" ? "🍽️" : "🏖️",
    time: f.time,
  }));
  const slotSections = useMemo(() => generateSlots(), []);

  const reset = () => {
    setForm(initial);
    setSubmitting(false);
    setDone(false);
  };

  const handleOpenChange = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(reset, 200);
  };

  const onPickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Image too large", { description: "Please keep it under 4MB." });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set("photo_data_url", String(reader.result || ""));
    reader.readAsDataURL(file);
  };

  const validate = () => {
    if (form.name.trim().length < 2) return "Tell us your name";
    const age = parseInt(form.age, 10);
    if (Number.isNaN(age) || age < 18 || age > 99) return "Age must be 18–99";
    if (!form.gender) return "Pick a gender option";
    if (!form.country.trim()) return "Tell us your country";
    if (!form.country_code || !/^\+\d{1,4}$/.test(form.country_code))
      return "Enter a valid country code (e.g. +91)";
    if (form.phone.replace(/\D/g, "").length < 6) return "Enter a valid WhatsApp number";
    if (!form.event || !form.event_date) return "Pick an event slot";
    if (form.hostel.trim().length < 2) return "Which hostel are you at?";
    if (!form.photo_data_url) return "Add a travel photo";
    if (form.plus_one && !form.plus_one_gender) return "Pick your +1's gender";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const res = await createSignup({
        name: form.name.trim(),
        age: parseInt(form.age, 10),
        gender: form.gender,
        country: form.country.trim(),
        country_code: form.country_code,
        phone: form.phone.replace(/\D/g, ""),
        event: form.event,
        event_date: form.event_date,
        hostel: form.hostel.trim(),
        photo_data_url: form.photo_data_url,
        women_only: form.women_only,
        plus_one: form.plus_one,
        plus_one_gender: form.plus_one ? form.plus_one_gender : undefined,
        referral_source: getRef() || undefined,
      });
      setDone(true);
      if (res.status === "already_signed_up") {
        toast.message("Already signed up", { description: res.message });
      } else {
        toast.success("You're in.", { description: "We'll WhatsApp you when your group is ready." });
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        toast.error("Too many attempts", { description: "Please try again in a little while." });
      } else if (status === 413 || status === 422) {
        toast.error("Couldn't sign you up", {
          description: Array.isArray(detail) ? detail[0]?.msg : "Check your inputs and try again.",
        });
      } else {
        toast.error("Something went wrong", { description: "Please try again." });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="signup-dialog"
        className="p-0 gap-0 bg-[#FDFBF7] border-[#E5E0D8] sm:max-w-[520px] max-h-[92vh] overflow-hidden flex flex-col rounded-3xl"
      >
        <DialogHeader className="px-6 pt-6 pb-4 text-left">
          <DialogTitle className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-[#1A1A1A]">
            {done ? "You're in." : "Sign up for Fourdays"}
          </DialogTitle>
          <DialogDescription className="text-[#5C5C5C] text-[15px]">
            {done
              ? "We'll WhatsApp you the moment your group is matched."
              : "Tell us a few quick things and we'll match you into a small group of 4–5 travellers."}
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="px-6 pb-7">
            <div className="flex items-start gap-3 rounded-2xl border border-[#E5E0D8] bg-white p-5">
              <CheckCircle2 className="h-6 w-6 shrink-0 text-[#FF5A36]" />
              <div className="text-[15px] text-[#1A1A1A] leading-relaxed">
                Thanks{form.name ? `, ${form.name.split(" ")[0]}` : ""}. We're curating your group for{" "}
                <span className="font-semibold">
                  {EVENTS.find((e) => e.value === form.event)?.label}
                </span>
                {form.event_date && (
                  <>
                    {" "}on{" "}
                    <span className="font-semibold">
                      {new Date(form.event_date + "T00:00:00").toLocaleDateString("en-GB", {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      })}
                    </span>
                  </>
                )}
                {" "}·{" "}
                <span className="font-semibold">
                  {EVENTS.find((e) => e.value === form.event)?.time}
                </span>
                . Expect a WhatsApp from us within a few hours.
              </div>
            </div>

            <a
              data-testid="signup-share-whatsapp"
              href={buildWhatsAppShareLink("signup-share")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold transition active:scale-[0.99]"
            >
              <Share2 className="h-4 w-4" />
              Share Fourdays with a friend
            </a>

            <Button
              data-testid="signup-close-button"
              onClick={() => handleOpenChange(false)}
              variant="ghost"
              className="mt-3 w-full h-11 rounded-xl text-[#5C5C5C] hover:text-[#1A1A1A]"
            >
              Done
            </Button>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            data-testid="signup-form"
            className="flex-1 overflow-y-auto px-6 pb-6 pt-1 space-y-5"
          >
            {/* Name & Age */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label htmlFor="su-name" className="text-xs font-semibold uppercase tracking-widest text-[#5C5C5C]">
                  Name
                </Label>
                <Input
                  id="su-name"
                  data-testid="signup-name"
                  placeholder="e.g. Aanya"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  className="h-11 bg-white border-[#E5E0D8] rounded-xl focus-visible:ring-[#FF5A36] focus-visible:ring-offset-0"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="su-age" className="text-xs font-semibold uppercase tracking-widest text-[#5C5C5C]">
                  Age
                </Label>
                <Input
                  id="su-age"
                  data-testid="signup-age"
                  type="number"
                  inputMode="numeric"
                  min="18"
                  max="99"
                  placeholder="24"
                  value={form.age}
                  onChange={(e) => set("age", e.target.value)}
                  className="h-11 bg-white border-[#E5E0D8] rounded-xl focus-visible:ring-[#FF5A36] focus-visible:ring-offset-0"
                />
              </div>
            </div>

            {/* Gender & Country */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C5C5C]">Gender</Label>
                <Select value={form.gender} onValueChange={(v) => set("gender", v)}>
                  <SelectTrigger
                    data-testid="signup-gender"
                    className="h-11 bg-white border-[#E5E0D8] rounded-xl focus:ring-[#FF5A36] focus:ring-offset-0"
                  >
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {GENDERS.map((g) => (
                      <SelectItem key={g.value} value={g.value} data-testid={`gender-${g.value}`}>
                        {g.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="su-country" className="text-xs font-semibold uppercase tracking-widest text-[#5C5C5C]">
                  Country
                </Label>
                <Input
                  id="su-country"
                  data-testid="signup-country"
                  placeholder="India"
                  value={form.country}
                  onChange={(e) => set("country", e.target.value)}
                  className="h-11 bg-white border-[#E5E0D8] rounded-xl focus-visible:ring-[#FF5A36] focus-visible:ring-offset-0"
                />
              </div>
            </div>

            {/* WhatsApp */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C5C5C]">
                WhatsApp number
              </Label>
              <div className="flex gap-2">
                <Input
                  data-testid="signup-country-code"
                  value={form.country_code}
                  onChange={(e) => set("country_code", e.target.value)}
                  placeholder="+91"
                  inputMode="tel"
                  autoComplete="tel-country-code"
                  className="h-11 w-[88px] bg-white border-[#E5E0D8] rounded-xl focus-visible:ring-[#FF5A36] focus-visible:ring-offset-0 font-semibold text-center"
                />
                <Input
                  data-testid="signup-phone"
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder="WhatsApp number"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  className="h-11 flex-1 bg-white border-[#E5E0D8] rounded-xl focus-visible:ring-[#FF5A36] focus-visible:ring-offset-0"
                />
              </div>
            </div>

            {/* Event slots — grouped by week, matching the app's "All events" layout */}
            <div className="space-y-3">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C5C5C]">
                Pick an event slot
              </Label>
              {slotSections.length === 0 ? (
                <div className="text-[13px] text-[#5C5C5C] bg-[#FDFBF7] border border-[#E5E0D8] rounded-xl px-3 py-2.5">
                  No slots available right now. Please check back tomorrow.
                </div>
              ) : (
                <div className="space-y-5" role="radiogroup" data-testid="signup-slot-group">
                  {slotSections.map((section) => (
                    <div key={section.group} className="space-y-2.5">
                      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#5C5C5C]">
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#FF5A36]" />
                        {section.group}
                      </div>
                      <div className="space-y-2.5">
                        {section.items.map((slot) => {
                          const active = form.event === slot.format && form.event_date === slot.iso;
                          const Icon = slot.icon;
                          return (
                            <button
                              key={slot.id}
                              type="button"
                              role="radio"
                              aria-checked={active}
                              data-testid={`signup-slot-${slot.id}`}
                              onClick={() =>
                                setForm((f) => ({ ...f, event: slot.format, event_date: slot.iso }))
                              }
                              className={`relative w-full text-left rounded-2xl border p-4 transition active:scale-[0.99] overflow-hidden ${
                                active
                                  ? "border-[#FF5A36] bg-[#FFF6F2] ring-2 ring-[#FF5A36]/20 shadow-[0_8px_24px_-14px_rgba(255,90,54,0.6)]"
                                  : "border-[#E5E0D8] bg-white hover:border-[#FF5A36]/60"
                              }`}
                            >
                              {/* brand accent bar on the left edge */}
                              <span
                                className={`absolute left-0 top-0 h-full w-1.5 transition-colors ${
                                  active ? "bg-[#FF5A36]" : "bg-transparent"
                                }`}
                              />
                              <div className="flex items-center justify-between gap-3 pl-1.5">
                                <div className="min-w-0">
                                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#5C5C5C]">
                                    {slot.dateLabel}
                                  </div>
                                  <div className="mt-1.5 flex items-center gap-2">
                                    <Icon
                                      className={`h-[18px] w-[18px] shrink-0 ${active ? "text-[#FF5A36]" : "text-[#1A1A1A]"}`}
                                    />
                                    <span className="font-display font-extrabold text-[18px] leading-none tracking-tight text-[#1A1A1A]">
                                      {slot.formatLabel}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2.5 shrink-0">
                                  <span
                                    className={`inline-flex items-center rounded-full px-3 py-1.5 text-[12px] font-bold tracking-tight transition-colors ${
                                      active ? "bg-[#FF5A36] text-white" : "bg-[#FFC857]/25 text-[#1A1A1A]"
                                    }`}
                                  >
                                    {slot.time}
                                  </span>
                                  {active && <CheckCircle2 className="h-[22px] w-[22px] text-[#FF5A36]" />}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>


            {/* Hostel */}
            <div className="space-y-1.5">
              <Label htmlFor="su-hostel" className="text-xs font-semibold uppercase tracking-widest text-[#5C5C5C]">
                Hostel / place you're staying at
              </Label>
              <Input
                id="su-hostel"
                data-testid="signup-hostel"
                placeholder="e.g. The Hosteller, Anjuna"
                value={form.hostel}
                onChange={(e) => set("hostel", e.target.value)}
                className="h-11 bg-white border-[#E5E0D8] rounded-xl focus-visible:ring-[#FF5A36] focus-visible:ring-offset-0"
              />
              <p className="text-[11px] text-[#5C5C5C]">
                We use this to make sure your group isn't all from the same hostel.
              </p>
            </div>

            {/* Photo */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C5C5C]">
                A travel photo of you
              </Label>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={onPickPhoto}
                className="hidden"
                data-testid="signup-photo-input"
              />
              {form.photo_data_url ? (
                <div className="flex items-center gap-3 rounded-2xl border border-[#E5E0D8] bg-white p-3">
                  <img
                    src={form.photo_data_url}
                    alt="Selected"
                    className="h-16 w-16 rounded-xl object-cover"
                  />
                  <div className="flex-1 text-sm text-[#1A1A1A] font-medium">Looks good.</div>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      set("photo_data_url", "");
                      if (fileRef.current) fileRef.current.value = "";
                    }}
                    data-testid="signup-photo-replace"
                    className="text-sm text-[#FF5A36] hover:text-[#E0411F] hover:bg-[#FFEDE6]"
                  >
                    Replace
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid="signup-photo-button"
                  className="w-full flex items-center gap-3 rounded-2xl border-2 border-dashed border-[#E5E0D8] bg-white p-4 hover:border-[#FF5A36]/60 hover:bg-[#FFEDE6]/30 transition"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#FFEDE6] text-[#FF5A36]">
                    <Camera className="h-5 w-5" />
                  </span>
                  <div className="text-left">
                    <div className="font-semibold text-[#1A1A1A] text-[15px]">Add a travel photo</div>
                    <div className="text-[12px] text-[#5C5C5C]">JPG/PNG · up to 4MB</div>
                  </div>
                  <Upload className="ml-auto h-4 w-4 text-[#5C5C5C]" />
                </button>
              )}
            </div>

            {/* +1 toggle */}
            <div className="flex items-start gap-3 rounded-2xl border border-[#E5E0D8] bg-white p-4">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#FFEDE6] text-[#FF5A36] shrink-0">
                <UserPlus className="h-4 w-4" />
              </span>
              <div className="flex-1">
                <Label htmlFor="plus-one" className="font-display font-bold text-[15px] text-[#1A1A1A]">
                  Bringing a +1?
                </Label>
                <p className="text-[12px] text-[#5C5C5C] mt-0.5">
                  Travelling with a friend or partner? Let us know so we can match the pair together.
                </p>
              </div>
              <Switch
                id="plus-one"
                data-testid="signup-plus-one"
                checked={form.plus_one}
                onCheckedChange={(v) => set("plus_one", v)}
                className="data-[state=checked]:bg-[#FF5A36]"
              />
            </div>
            {form.plus_one && (
              <div className="space-y-1.5 -mt-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C5C5C]">
                  Your +1's gender
                </Label>
                <Select value={form.plus_one_gender} onValueChange={(v) => set("plus_one_gender", v)}>
                  <SelectTrigger
                    data-testid="signup-plus-one-gender"
                    className="h-11 bg-white border-[#E5E0D8] rounded-xl focus:ring-[#FF5A36] focus:ring-offset-0"
                  >
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    {GENDERS.map((g) => (
                      <SelectItem key={g.value} value={g.value} data-testid={`plus-one-gender-${g.value}`}>
                        {g.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Women-only toggle — always visible, enabled only when gender = female */}
            {(() => {
              const enabled = form.gender === "female";
              return (
                <div
                  className={`flex items-start gap-3 rounded-2xl border border-[#E5E0D8] bg-white p-4 transition ${
                    enabled ? "opacity-100" : "opacity-60"
                  }`}
                >
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#FFEDE6] text-[#FF5A36] shrink-0">
                    <ShieldCheck className="h-4 w-4" />
                  </span>
                  <div className="flex-1">
                    <Label htmlFor="women-only" className="font-display font-bold text-[15px] text-[#1A1A1A]">
                      Women-only group
                    </Label>
                    <p className="text-[12px] text-[#5C5C5C] mt-0.5">
                      {enabled
                        ? "Match me only into a group of women."
                        : "Available when gender is set to Female."}
                    </p>
                  </div>
                  <Switch
                    id="women-only"
                    data-testid="signup-women-only"
                    checked={form.women_only}
                    disabled={!enabled}
                    onCheckedChange={(v) => set("women_only", v)}
                    className="data-[state=checked]:bg-[#FF5A36]"
                  />
                </div>
              );
            })()}

            <Button
              type="submit"
              data-testid="signup-submit"
              disabled={submitting}
              className="w-full h-12 rounded-xl bg-[#FF5A36] hover:bg-[#E0411F] active:scale-[0.99] transition text-white font-semibold text-base"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Sending
                </>
              ) : (
                "Sign me up"
              )}
            </Button>
            <p className="text-[11px] text-center text-[#5C5C5C]">
              By signing up you agree to be contacted on WhatsApp by Fourdays and to our{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="signup-privacy-link"
                className="text-[#FF5A36] font-semibold hover:underline"
              >
                Privacy Policy
              </a>{" "}
              and{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="signup-terms-link"
                className="text-[#FF5A36] font-semibold hover:underline"
              >
                Terms of Service
              </a>
              .
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
